import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Save, Trash2 } from 'lucide-react';
import {
  ApiError,
  api,
  canEdit,
  type AuthUser,
  type MType,
  type StrengthRequestRow,
} from '@/lib/api';

const M_TYPES: MType[] = ['planes', 'rockets', 'tanks'];

interface DraftValues {
  m1Power: string;
  m1Type: MType;
  m2Power: string;
  m2Type: MType;
  m3Power: string;
  m3Type: MType;
}

const emptyDraft = (): DraftValues => ({
  m1Power: '',
  m1Type: 'planes',
  m2Power: '',
  m2Type: 'rockets',
  m3Power: '',
  m3Type: 'tanks',
});

export default function StrengthRequestDetail({ user }: { user: AuthUser }) {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const editable = canEdit(user.role);
  const qc = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['strength-request', id],
    queryFn: () => api.getStrengthRequest(id),
    enabled: Number.isFinite(id),
  });

  const [search, setSearch] = useState('');
  const [onlyPending, setOnlyPending] = useState(true);

  const closeMutation = useMutation({
    mutationFn: () => api.updateStrengthRequest(id, { status: 'closed' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['strength-request', id] });
      qc.invalidateQueries({ queryKey: ['strength-requests'] });
    },
  });
  const reopenMutation = useMutation({
    mutationFn: () => api.updateStrengthRequest(id, { status: 'open' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['strength-request', id] });
      qc.invalidateQueries({ queryKey: ['strength-requests'] });
    },
  });

  const filtered = useMemo(() => {
    const rows = detailQuery.data?.rows ?? [];
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (onlyPending && row.submitted) return false;
      if (!term) return true;
      const a = row.player.name.toLowerCase();
      const b = (row.player.displayName ?? '').toLowerCase();
      return a.includes(term) || b.includes(term);
    });
  }, [detailQuery.data, search, onlyPending]);

  if (!Number.isFinite(id)) return <div>Invalid request id.</div>;
  if (detailQuery.isLoading)
    return <div className="text-slate-400">Loading…</div>;
  if (!detailQuery.data)
    return <div className="text-slate-400">Not found.</div>;

  const { request } = detailQuery.data;
  const closed = request.status === 'closed';

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/strength-requests"
            className="text-xs text-slate-400 hover:text-slate-200"
          >
            ← All requests
          </Link>
          <h1 className="mt-1 text-xl font-semibold">{request.title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span>Started {request.startedAt.slice(0, 10)}</span>
            {request.closedAt && <span>Closed {request.closedAt.slice(0, 10)}</span>}
            <span
              className={
                closed
                  ? 'rounded bg-slate-700 px-2 py-0.5 text-slate-300'
                  : 'rounded bg-emerald-700/50 px-2 py-0.5 text-emerald-100'
              }
            >
              {request.status}
            </span>
            <span className="tabular-nums">
              {request.counts.submitted} / {request.counts.total} submitted
            </span>
          </div>
        </div>
        {editable && (
          <div className="flex gap-2">
            {closed ? (
              <button
                onClick={() => reopenMutation.mutate()}
                className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
              >
                Reopen
              </button>
            ) : (
              <button
                onClick={() => closeMutation.mutate()}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
              >
                Close request
              </button>
            )}
          </div>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / displayname…"
          className="input max-w-xs"
        />
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
          />
          Only pending
        </label>
        <div className="ml-auto text-xs text-slate-500">
          {filtered.length} shown
        </div>
      </div>

      {/* Mobile: card layout with big inputs. */}
      <div className="space-y-2 md:hidden">
        {filtered.map((row) => (
          <PlayerCard
            key={row.player.id}
            row={row}
            requestId={id}
            editable={editable}
            closed={closed}
          />
        ))}
        {filtered.length === 0 && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-6 text-center text-sm text-slate-500">
            No players match.
          </div>
        )}
      </div>

      {/* Desktop: dense table. */}
      <div className="hidden overflow-x-auto rounded-lg border border-slate-800 bg-slate-900/40 md:block">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-slate-900/70 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2 text-left">Player</th>
              <th className="px-3 py-2 text-right">M1</th>
              <th className="px-3 py-2 text-right">M2</th>
              <th className="px-3 py-2 text-right">M3</th>
              <th className="px-3 py-2 text-right">M-Sum</th>
              <th className="px-3 py-2 text-left">Saved</th>
              {editable && !closed && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <PlayerRow
                key={row.player.id}
                row={row}
                requestId={id}
                editable={editable}
                closed={closed}
              />
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={editable && !closed ? 7 : 6}
                  className="px-3 py-6 text-center text-slate-500"
                >
                  No players match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Style />
    </div>
  );
}

function PlayerRow({
  row,
  requestId,
  editable,
  closed,
}: {
  row: StrengthRequestRow;
  requestId: number;
  editable: boolean;
  closed: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftValues>(() => {
    const s = row.snapshot ?? row.latestSnapshot;
    if (!s) return emptyDraft();
    return {
      m1Power: String(s.m1Power),
      m1Type: s.m1Type,
      m2Power: String(s.m2Power),
      m2Type: s.m2Type,
      m3Power: String(s.m3Power),
      m3Type: s.m3Type,
    };
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const parsed = parseDraft(draft);
      if (!parsed) throw new ApiError(400, 'Fill all values');
      return api.addStrengthRequestEntry(requestId, {
        playerId: row.player.id,
        ...parsed,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['strength-request', requestId] });
      qc.invalidateQueries({ queryKey: ['strength-requests'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to save'),
  });

  const canSubmit = editable && !closed;
  const s = row.snapshot;
  const mSum = s ? s.m1Power + s.m2Power + s.m3Power : null;

  return (
    <tr className={row.submitted ? 'bg-emerald-950/20' : ''}>
      <td className="px-3 py-2">
        <div className="font-medium">{row.player.name}</div>
        {row.player.displayName && (
          <div className="text-xs text-slate-500">
            {row.player.displayName} · {row.player.rank}
          </div>
        )}
      </td>
      {canSubmit ? (
        <>
          <MCell
            power={draft.m1Power}
            mtype={draft.m1Type}
            onPower={(v) => setDraft((d) => ({ ...d, m1Power: v }))}
            onType={(t) => setDraft((d) => ({ ...d, m1Type: t }))}
          />
          <MCell
            power={draft.m2Power}
            mtype={draft.m2Type}
            onPower={(v) => setDraft((d) => ({ ...d, m2Power: v }))}
            onType={(t) => setDraft((d) => ({ ...d, m2Type: t }))}
          />
          <MCell
            power={draft.m3Power}
            mtype={draft.m3Type}
            onPower={(v) => setDraft((d) => ({ ...d, m3Power: v }))}
            onType={(t) => setDraft((d) => ({ ...d, m3Type: t }))}
          />
          <td className="px-3 py-2 text-right tabular-nums text-slate-400">
            {previewMSum(draft)}
          </td>
          <td className="px-3 py-2 text-xs text-slate-500">
            {s ? s.recordedAt.slice(0, 16).replace('T', ' ') : '—'}
            {error && <div className="text-red-400">{error}</div>}
          </td>
          <td className="px-2 py-1">
            <button
              type="button"
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              disabled={save.isPending}
              className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs hover:bg-indigo-500 disabled:opacity-50"
            >
              <Save size={12} /> Save
            </button>
          </td>
        </>
      ) : s ? (
        <>
          <td className="px-3 py-2 text-right tabular-nums">
            {s.m1Power.toFixed(2)} <span className="text-xs text-slate-500">{s.m1Type}</span>
          </td>
          <td className="px-3 py-2 text-right tabular-nums">
            {s.m2Power.toFixed(2)} <span className="text-xs text-slate-500">{s.m2Type}</span>
          </td>
          <td className="px-3 py-2 text-right tabular-nums">
            {s.m3Power.toFixed(2)} <span className="text-xs text-slate-500">{s.m3Type}</span>
          </td>
          <td className="px-3 py-2 text-right tabular-nums">
            {(mSum ?? 0).toFixed(2)}
          </td>
          <td className="px-3 py-2 text-xs text-slate-500">
            {s.recordedAt.slice(0, 16).replace('T', ' ')}
          </td>
        </>
      ) : (
        <td colSpan={5} className="px-3 py-2 text-center text-xs text-slate-500">
          — pending —
        </td>
      )}
    </tr>
  );
}

function MCell({
  power,
  mtype,
  onPower,
  onType,
}: {
  power: string;
  mtype: MType;
  onPower: (v: string) => void;
  onType: (t: MType) => void;
}) {
  return (
    <td className="px-2 py-1">
      <div className="flex gap-1">
        <input
          type="text"
          inputMode="decimal"
          className="input text-right"
          value={power}
          onChange={(e) => onPower(e.target.value.replace(',', '.'))}
        />
        <select
          value={mtype}
          onChange={(e) => onType(e.target.value as MType)}
          className="input w-24"
        >
          {M_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
    </td>
  );
}

function PlayerCard({
  row,
  requestId,
  editable,
  closed,
}: {
  row: StrengthRequestRow;
  requestId: number;
  editable: boolean;
  closed: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<DraftValues>(() => {
    const s = row.snapshot ?? row.latestSnapshot;
    if (!s) return emptyDraft();
    return {
      m1Power: String(s.m1Power),
      m1Type: s.m1Type,
      m2Power: String(s.m2Power),
      m2Type: s.m2Type,
      m3Power: String(s.m3Power),
      m3Type: s.m3Type,
    };
  });
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(true);

  const save = useMutation({
    mutationFn: () => {
      const parsed = parseDraft(draft);
      if (!parsed) throw new ApiError(400, 'Fill all values');
      return api.addStrengthRequestEntry(requestId, {
        playerId: row.player.id,
        ...parsed,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['strength-request', requestId] });
      qc.invalidateQueries({ queryKey: ['strength-requests'] });
      setCollapsed(true);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to save'),
  });

  const canSubmit = editable && !closed;
  const s = row.snapshot;
  const mSum = previewMSum(draft);

  return (
    <div
      className={
        'rounded-lg border border-slate-800 bg-slate-900/40 p-3 ' +
        (row.submitted ? 'ring-1 ring-emerald-800/40' : '')
      }
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <div>
          <div className="font-medium">{row.player.name}</div>
          <div className="text-xs text-slate-500">
            {row.player.displayName ? `${row.player.displayName} · ` : ''}
            {row.player.rank}
          </div>
        </div>
        <div className="text-right text-xs">
          {row.submitted ? (
            <div className="text-emerald-400">✓ submitted</div>
          ) : (
            <div className="text-slate-500">— pending —</div>
          )}
          {s && (
            <div className="mt-0.5 text-slate-500 tabular-nums">
              {(s.m1Power + s.m2Power + s.m3Power).toFixed(2)}
            </div>
          )}
        </div>
      </button>

      {!collapsed && (
        <div className="mt-3 space-y-3">
          {canSubmit ? (
            <>
              {(['m1', 'm2', 'm3'] as const).map((key) => {
                const powerKey = `${key}Power` as const;
                const typeKey = `${key}Type` as const;
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="w-8 text-xs font-medium uppercase tracking-wider text-slate-400">
                        {key.toUpperCase()}
                      </div>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={draft[powerKey]}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            [powerKey]: e.target.value.replace(',', '.'),
                          }))
                        }
                        style={{
                          color: '#e2e8f0',
                          WebkitTextFillColor: '#e2e8f0',
                          caretColor: '#e2e8f0',
                          backgroundColor: '#020617',
                          fontSize: '16px',
                        }}
                        className="min-w-0 flex-1 rounded border border-slate-600 px-3 py-2.5 text-right"
                      />
                    </div>
                    <div className="flex gap-1 pl-10">
                      {M_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setDraft((d) => ({ ...d, [typeKey]: t }))
                          }
                          className={
                            'flex-1 rounded border px-2 py-1.5 text-xs capitalize ' +
                            (draft[typeKey] === t
                              ? 'border-indigo-500 bg-indigo-600/30 text-indigo-100'
                              : 'border-slate-700 bg-slate-950 text-slate-400')
                          }
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>
                  M-Sum:{' '}
                  <span className="text-slate-300 tabular-nums">{mSum}</span>
                </span>
                {s && (
                  <span>
                    Last: {s.recordedAt.slice(0, 16).replace('T', ' ')}
                  </span>
                )}
              </div>
              {error && <div className="text-sm text-red-400">{error}</div>}
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  save.mutate();
                }}
                disabled={save.isPending}
                className="flex w-full items-center justify-center gap-2 rounded bg-indigo-600 py-2.5 text-sm font-medium hover:bg-indigo-500 disabled:opacity-50"
              >
                <Save size={14} /> Save
              </button>
            </>
          ) : s ? (
            <div className="grid grid-cols-3 gap-2 text-sm">
              <MReadCell label="M1" value={s.m1Power} type={s.m1Type} />
              <MReadCell label="M2" value={s.m2Power} type={s.m2Type} />
              <MReadCell label="M3" value={s.m3Power} type={s.m3Type} />
            </div>
          ) : (
            <div className="text-center text-xs text-slate-500">
              — no snapshot yet —
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MReadCell({
  label,
  value,
  type,
}: {
  label: string;
  value: number;
  type: MType;
}) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="tabular-nums">{value.toFixed(2)}</div>
      <div className="text-[10px] text-slate-500">{type}</div>
    </div>
  );
}

function parseDraft(d: DraftValues) {
  const m1 = Number(d.m1Power);
  const m2 = Number(d.m2Power);
  const m3 = Number(d.m3Power);
  if (![m1, m2, m3].every((n) => Number.isFinite(n) && n >= 0)) return null;
  return {
    m1Power: m1,
    m1Type: d.m1Type,
    m2Power: m2,
    m2Type: d.m2Type,
    m3Power: m3,
    m3Type: d.m3Type,
  };
}

function previewMSum(d: DraftValues) {
  const m1 = Number(d.m1Power);
  const m2 = Number(d.m2Power);
  const m3 = Number(d.m3Power);
  if (![m1, m2, m3].every(Number.isFinite)) return '—';
  return (m1 + m2 + m3).toFixed(2);
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: rgb(226 232 240);
        -webkit-text-fill-color: rgb(226 232 240);
        caret-color: rgb(226 232 240);
        appearance: none;
        -webkit-appearance: none;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
      .input::placeholder { color: rgb(100 116 139); }
      select.input {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2394a3b8'%3e%3cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right 6px center;
        background-size: 16px;
        padding-right: 26px;
      }
    `}</style>
  );
}
